
import React from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, IconButton,
  Divider
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { LoadingSpinner } from '../../Components/spinner/spinner';
import { useTranslation } from 'react-i18next';

export const AFEViewModal = ({ 
  open, 
  onClose, 
  selectedItem, 
  modalLoading,
  formatList 
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('assignedFE.viewDetails')}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 4 }}>
        {modalLoading ? (
          <LoadingSpinner size={30} />
        ) : selectedItem ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Name of FE */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {t('assignedFE.assignedFE1')}:
              </Typography>
              <Typography variant="body1" sx={{ pl: 2 }}>{selectedItem.name_of_fe || '-'}</Typography>
            </Box>

            {/* CA Details */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {t('assignedFE.nameOfCA')}
              </Typography>
              <Typography variant="body1" sx={{ pl: 2, mb: 1 }}>
                {selectedItem.name_of_ca || '-'}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {t('assignedFE.designation')}
              </Typography>
              <Typography variant="body1" sx={{ pl: 2 }}>
                {selectedItem.designation || '-'}
              </Typography>
            </Box>

            {/* Addresses */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {t('assignedFE.caAddress')}:
              </Typography>
              <Typography variant="body1" sx={{ pl: 2 }}>
                {selectedItem.ca_address || '-'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {t('assignedFE.address')}:
              </Typography>
              <Typography variant="body1" sx={{ pl: 2 }}>
                {selectedItem.address_of_company || '-'}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Contact Info */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {t('assignedFE.phoneNo')}:
                </Typography>
                <Typography variant="body1" sx={{ pl: 2 }}>{selectedItem.ph_number || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {t('assignedFE.email')}:
                </Typography>
                <Typography variant="body1" sx={{ pl: 2 }} component="a" href={`mailto:${selectedItem.email}`}>
                  {selectedItem.email || '-'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Company Contact */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {t('assignedFE.companyPhone')}:
                </Typography>
                <Typography variant="body1" sx={{ pl: 2 }}>{selectedItem.company_ph_number || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {t('assignedFE.companyEmail')}:
                </Typography>
                <Typography variant="body1" sx={{ pl: 2 }} component="a" href={`mailto:${selectedItem.company_email}`}>
                  {selectedItem.company_email || '-'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Optional Fields */}
            {selectedItem.group_of_entities && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {t('assignedFE.groupOfEntities')}:
                </Typography>
                <Typography variant="body1" sx={{ pl: 2, whiteSpace: 'pre-line' }}>
                  {formatList(selectedItem.group_of_entities)}
                </Typography>
              </Box>
            )}

            {selectedItem.name_of_directors && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {t('assignedFE.nameOfDirectors')}:
                </Typography>
                <Typography variant="body1" sx={{ pl: 2, whiteSpace: 'pre-line' }}>
                  {formatList(selectedItem.name_of_directors)}
                </Typography>
              </Box>
            )}

            {selectedItem.crime_no && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {t('assignedFE.crimeNo')}:
                </Typography>
                <Typography variant="body1" sx={{ pl: 2 }}>{selectedItem.crime_no}</Typography>
              </Box>
            )}

            {selectedItem.fir_no && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {t('assignedFE.firNo')}:
                </Typography>
                <Typography variant="body1" sx={{ pl: 2 }}>{selectedItem.fir_no}</Typography>
              </Box>
            )}

            {selectedItem.link && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {t('assignedFE.caAppointmentLetter')}:
                </Typography>
                <a 
                  href={selectedItem.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 'bold', display: 'inline-block' }}
                >
                  {t('assignedFE.viewDetails')}
                </a>
              </Box>
            )}
          </Box>
        ) : (
          <Typography>{t('assignedFE.noData')}</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};
